// Starts an operation job through POST /jobs with one Idempotency-Key per attempt (kept for retries, renewed after a job is created), refreshes the job queries and confirms with a toast that links to the job.
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useRef } from "react";

import type { JobRequest, OperationJob } from "@/api/__generated__";
import { jobsCreateJobMutation } from "@/api/__generated__/@tanstack/react-query.gen";
import { notify } from "@/components/ui/sonner";
import { jobTarget, JOB_KIND_NAMES } from "@/features/jobs/labels";
import { invalidateJobQueries } from "@/features/jobs/queries";

export interface StartJob {
  start: (body: JobRequest) => void;
  reset: () => void;
  pending: boolean;
  error: unknown;
  job: OperationJob | undefined;
}

export function useStartJob(onCreated?: (job: OperationJob) => void): StartJob {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const key = useRef<string | null>(null);
  const mutation = useMutation({
    ...jobsCreateJobMutation(),
    onSuccess: (job) => {
      key.current = null;
      void invalidateJobQueries(queryClient);
      notify(`${JOB_KIND_NAMES[job.kind]} started`, jobTarget(job), {
        action: {
          label: "View job",
          onClick: () => {
            void navigate({ to: "/ops/jobs/$jobId", params: { jobId: job.job_id } });
          },
        },
      });
      onCreated?.(job);
    },
  });
  const { mutate, reset: resetMutation } = mutation;
  const start = useCallback(
    (body: JobRequest) => {
      key.current ??= crypto.randomUUID();
      mutate({ body, headers: { "Idempotency-Key": key.current } });
    },
    [mutate],
  );
  const reset = useCallback(() => {
    key.current = null;
    resetMutation();
  }, [resetMutation]);
  return { start, reset, pending: mutation.isPending, error: mutation.error, job: mutation.data };
}
